import { useState } from 'react';
import { UserCheck, FileText, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { exportStudentReport } from '@/services/adminAttendanceService';
import type { AdminAttendanceFilters } from '@/types/admin-attendance.types';

type ReportScope = 'current' | 'all';

interface StudentReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
  studentCode: string;
  filters: AdminAttendanceFilters;
}

export function StudentReportModal({ isOpen, onClose, studentId, studentName, studentCode, filters }: StudentReportModalProps) {
  const [scope, setScope] = useState<ReportScope>('current');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  if (!isOpen) return null;

  const handleGenerate = async () => {
    setStatus('loading');
    setErrorMsg('');
    try {
      await exportStudentReport(studentId, filters, scope);
      setStatus('success');
      setTimeout(() => {
        setStatus('idle');
        onClose();
      }, 1500);
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message || 'Erreur lors de la generation du rapport');
    }
  };

  const handleClose = () => {
    if (status === 'loading') return;
    setStatus('idle');
    setErrorMsg('');
    setScope('current');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={handleClose}>
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
        style={{ animation: 'modalPop 0.3s ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`
          @keyframes modalPop { 0% { transform: scale(0.9); opacity: 0; } 50% { transform: scale(1.02); } 100% { transform: scale(1); opacity: 1; } }
          @keyframes iconBounce { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
        `}</style>

        {/* Close button */}
        <div className="flex justify-end p-3 pb-0">
          <button onClick={handleClose} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors" disabled={status === 'loading'}>
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Icon */}
        <div className="flex justify-center -mt-1">
          <div
            className="p-5 rounded-full bg-gradient-to-br from-indigo-100 to-indigo-200 ring-8 ring-indigo-50"
            style={{ animation: 'iconBounce 0.5s ease-out' }}
          >
            {status === 'success' ? (
              <CheckCircle size={36} className="text-green-500" strokeWidth={2} />
            ) : status === 'error' ? (
              <AlertCircle size={36} className="text-red-500" strokeWidth={2} />
            ) : (
              <UserCheck size={36} className="text-indigo-500" strokeWidth={2} />
            )}
          </div>
        </div>

        {/* Title */}
        <div className="text-center mt-4 px-6">
          <h3 className="text-lg font-bold text-gray-900">
            {status === 'success' ? 'Rapport genere !' : status === 'error' ? 'Erreur' : "Rapport d'Assiduite"}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {status === 'success'
              ? 'Le rapport PDF a ete telecharge.'
              : status === 'error'
              ? errorMsg
              : 'Generez un rapport PDF individuel'}
          </p>
        </div>

        {status === 'idle' || status === 'loading' ? (
          <>
            {/* Student info */}
            <div className="px-6 mt-4">
              <div className="bg-gray-50 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-indigo-600 font-bold text-sm">
                    {studentName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">{studentName}</div>
                  <div className="text-xs text-gray-500">{studentCode}</div>
                </div>
              </div>
            </div>

            {/* Scope selection */}
            <div className="px-6 mt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Periode du rapport</p>
              <div className="space-y-2">
                <button
                  onClick={() => setScope('current')}
                  disabled={status === 'loading'}
                  className={`w-full p-3 rounded-xl border-2 transition-all text-left flex items-center gap-3 ${
                    scope === 'current'
                      ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    scope === 'current' ? 'border-indigo-500' : 'border-gray-300'
                  }`}>
                    {scope === 'current' && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">Semestre en cours</div>
                    <div className="text-xs text-gray-500">Donnees du semestre selectionne</div>
                  </div>
                </button>

                <button
                  onClick={() => setScope('all')}
                  disabled={status === 'loading'}
                  className={`w-full p-3 rounded-xl border-2 transition-all text-left flex items-center gap-3 ${
                    scope === 'all'
                      ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    scope === 'all' ? 'border-indigo-500' : 'border-gray-300'
                  }`}>
                    {scope === 'all' && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">Tout l'historique</div>
                    <div className="text-xs text-gray-500">Toutes les donnees disponibles</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 px-6 py-5 mt-2">
              <button
                onClick={handleClose}
                disabled={status === 'loading'}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleGenerate}
                disabled={status === 'loading'}
                className="flex-1 px-4 py-2.5 rounded-xl bg-indigo-600 text-sm font-medium text-white hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Generation...
                  </>
                ) : (
                  <>
                    <FileText size={16} />
                    Generer le PDF
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="flex justify-center px-6 py-5 mt-2">
            <button
              onClick={handleClose}
              className="px-8 py-2.5 rounded-xl bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
            >
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
